import React, { useEffect } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.js';
import Footer from '../components/Footer.jsx';
import { applyPageSeo } from '../utils/seo.js';
import { withLinks } from '../utils/textLinks.jsx';
import { companyContact } from '../config/company.js';

// The Veridion page in the order of the story: the vision (the claim, the
// category reframed), immediately the capability that explains the claim (what
// it cannot do, it creates), then how it integrates into every process, the
// units that own whole fields, what it can be relied on for, the
// everyday work, operation, and the enquiry. Every strong line appears exactly
// once. All text lives in translations/pages/veridion/<language>.js.
const SectionHead = ({ title, subtitle, currentLang }) => (
  <header className="site-section-head">
    <h2>{title}</h2>
    {subtitle && <p>{withLinks(subtitle, currentLang)}</p>}
  </header>
);

const VeridionProductPage = () => {
  const { currentLang, getPageTranslations } = useTranslation('veridion');
  const t = getPageTranslations('veridion');

  useEffect(() => {
    applyPageSeo({ ...t.seo, pageId: 'veridion' }, currentLang);
  }, [t.seo, currentLang]);

  const mailTo = (subject) => `mailto:${companyContact.email}?subject=${encodeURIComponent(subject || '')}`;

  return (
    <div className="min-h-screen bg-bg-main text-text-primary">
      {/* The vision. */}
      <section className="page-head pb-10 md:pb-14">
        <div className="max-w-7xl mx-auto px-4">
          <div>
            <div className="editorial-kicker mb-4">{t.hero?.kicker}</div>
            <h1 className="site-title">
              {t.hero?.title}
            </h1>
            <p className="site-lead">
              {t.hero?.claim}
            </p>
            <p className="site-copy">{withLinks(t.hero?.lead || '', currentLang)}</p>
            <a href={mailTo(t.cta?.subject)} className="homepage-button-primary page-head__action inline-flex items-center justify-center gap-2">
              <Mail size={18} aria-hidden="true" />
              {t.cta?.button}
            </a>
          </div>
        </div>
      </section>

      {/* The category, reframed in one idea. */}
      <section className="pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="border-t border-b border-border py-8 md:py-12">
            {t.vision?.lines?.map((line, index) => (
              <p
                key={index}
                className={`text-2xl md:text-4xl leading-snug tracking-[-0.03em] ${
                  index === (t.vision?.lines?.length || 0) - 1 ? 'font-black text-text-primary' : 'font-light text-text-muted'
                }`}
              >
                {line}
              </p>
            ))}
            <p className="text-lg text-text-secondary leading-relaxed max-w-4xl mt-6">{t.vision?.note}</p>
          </div>
        </div>
      </section>

      {/* The capability that explains the claim: what it cannot do, it creates. */}
      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-6xl font-black tracking-[-0.05em] leading-[0.98] text-text-primary mb-6">
            {t.grow?.title}
          </h2>
          <p className="text-lg text-text-secondary leading-relaxed max-w-3xl mx-auto mb-5">{withLinks(t.grow?.text || '', currentLang)}</p>
          <p className="text-xl md:text-2xl font-black tracking-[-0.02em] text-text-primary">{t.grow?.punch}</p>
        </div>
      </section>

      {/* It integrates into every process. */}
      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHead title={t.integrate?.title} subtitle={t.integrate?.subtitle} currentLang={currentLang} />
          <div className="grid md:grid-cols-3 gap-5">
            {t.integrate?.cards?.map((card, index) => (
              <article key={index} className="homepage-card p-6 md:p-7">
                <h3 className="text-xl font-black tracking-[-0.025em] text-text-primary mb-2">{card.title}</h3>
                <p className="text-text-secondary leading-relaxed">{withLinks(card.text, currentLang)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* The units own whole fields. */}
      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHead title={t.org?.title} subtitle={t.org?.subtitle} currentLang={currentLang} />
          <article className="homepage-card p-6 md:p-8 bg-bg-hover mb-5">
            <p className="text-sm font-black uppercase tracking-[0.16em] text-primary-light mb-3">{t.org?.execKicker}</p>
            <h3 className="text-2xl md:text-3xl font-black tracking-[-0.04em] text-text-primary mb-3">{t.org?.execTitle}</h3>
            <p className="text-text-secondary leading-relaxed max-w-4xl">{withLinks(t.org?.execText || '', currentLang)}</p>
          </article>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.org?.units?.map((unit, index) => (
              <article key={index} className="homepage-card p-5 md:p-6">
                <h3 className="text-xl font-black tracking-[-0.025em] text-text-primary mb-2">{unit.name}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{withLinks(unit.text, currentLang)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Plannable, game-theoretic, explainable, learning. */}
      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHead title={t.trust?.title} currentLang={currentLang} />
          <div className="grid md:grid-cols-2 gap-5">
            {t.trust?.cards?.map((card, index) => (
              <article key={index} className="homepage-card p-6 md:p-7">
                <h3 className="text-2xl font-black tracking-[-0.03em] text-text-primary mb-3">{card.title}</h3>
                <p className="text-text-secondary leading-relaxed">{withLinks(card.text, currentLang)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* The everyday work. */}
      <section className="homepage-section homepage-muted-panel">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHead title={t.daily?.title} subtitle={t.daily?.subtitle} currentLang={currentLang} />
          <div className="grid md:grid-cols-2 gap-4">
            {t.daily?.items?.map((item, index) => (
              <article key={index} className="homepage-card p-5 md:p-6">
                <h3 className="text-lg font-black tracking-[-0.02em] text-text-primary mb-2">{item.title}</h3>
                <p className="text-text-secondary text-sm leading-relaxed">{withLinks(item.text, currentLang)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Operation: at yours, and at ours. */}
      <section className="homepage-section">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHead title={t.operate?.title} />
          <div className="grid md:grid-cols-2 gap-5">
            {t.operate?.cards?.map((card, index) => (
              <article key={index} className="homepage-card p-6 md:p-7">
                <h3 className="text-xl font-black tracking-[-0.025em] text-text-primary mb-2">{card.title}</h3>
                <p className="text-text-secondary leading-relaxed">{withLinks(card.text, currentLang)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* The enquiry. */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="site-contact">
            <div>
              <h2 className="site-heading">{t.cta?.title}</h2>
              {t.cta?.subtitle && <p className="finance-cta__lead">{t.cta?.subtitle}</p>}
            </div>
            <a href={mailTo(t.cta?.subject)} className="homepage-button-primary">
              {t.cta?.button}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VeridionProductPage;
